import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SummaryApi from "../../../common";

export default function SchoolManager() {
  const [schools, setSchools] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    schoolName: "",
    contact1: "",
    contact2: "",
    projectName: "",
    amount: "",
    address: "",
  });

  const navigate = useNavigate();

  // ✅ Fetch schools
  const fetchSchools = async () => {
    try {
      const res = await fetch(SummaryApi.schools.getAll.url);
      const data = await res.json();
      setSchools(data);
    } catch (err) {
      console.error("Error fetching schools:", err);
    }
  };

  // ✅ Fetch all transactions
  const fetchTransactions = async () => {
    try {
      const res = await fetch(SummaryApi.schoolTransactions.getAll.url);
      const data = await res.json();
      setTransactions(data);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  useEffect(() => {
    fetchSchools();
    fetchTransactions();
  }, []);

const getPaidAmount = (schoolId) => {
  const schoolTransactions = transactions.filter(
    (t) => t.schoolId?._id === schoolId
  );

  return schoolTransactions.reduce((total, transaction) => {
    const paidSum = transaction.installments
      ?.filter((inst) => inst.status === "Paid")
      .reduce((sum, inst) => sum + Number(inst.amount || 0), 0);
    return total + paidSum;
  }, 0);
};

const getPendingAmount = (schoolId) => {
  const schoolTransactions = transactions.filter(
    (t) => t.schoolId?._id === schoolId
  );

  return schoolTransactions.reduce((total, transaction) => {
    const pendingSum = transaction.installments
      ?.filter((inst) => inst.status !== "Paid")
      .reduce((sum, inst) => sum + Number(inst.amount || 0), 0);
    return total + pendingSum;
  }, 0);
};


  // ✅ Handle input change
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ Handle submit (Create / Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const api = editingId
        ? SummaryApi.schools.update(editingId)
        : SummaryApi.schools.create;

      const res = await fetch(api.url, {
        method: api.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("Failed to save school");

      await fetchSchools();
      setShowModal(false);
      setEditingId(null);
      setForm({
        schoolName: "",
        contact1: "",
        contact2: "",
        projectName: "",
        amount: "",
        address: "",
      });
    } catch (err) {
      console.error("Error saving school:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Edit school
  const handleEdit = (school) => {
    setEditingId(school._id);
    setForm({
      schoolName: school.schoolName,
      contact1: school.contact1,
      contact2: school.contact2 || "",
      projectName: school.projectName,
      amount: school.amount,
      address: school.address,
    });
    setShowModal(true);
  };

  // ✅ Delete school
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this school?")) return;
    try {
      const api = SummaryApi.schools.delete(id);
      const res = await fetch(api.url, { method: api.method });

      if (!res.ok) throw new Error("Failed to delete school");

      await fetchSchools();
    } catch (err) {
      console.error("Error deleting school:", err);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">School Management</h1>
        <button
          onClick={() => {
            setEditingId(null);
            setForm({
              schoolName: "",
              contact1: "",
              contact2: "",
              projectName: "",
              amount: "",
              address: "",
            });
            setShowModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow"
        >
          + Create School
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-100 text-gray-800 uppercase text-xs">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">School Name</th>
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">Total Amount</th>
              <th className="px-4 py-3">Paid Amount</th>
              <th className="px-4 py-3">Pending Amount</th>
              <th className="px-4 py-3">Contact 1</th>
              <th className="px-4 py-3">Contact 2</th>
              <th className="px-4 py-3">Address</th>
              <th className="px-4 py-3 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {schools.length > 0 ? (
              schools.map((school, idx) => {
                const paidAmount = getPaidAmount(school._id);
                const pendingAmount = getPendingAmount(school._id);

                
                return (
                  <tr
                    key={school._id}
                    className="border-t hover:bg-gray-50 transition"
                  >
                    <td className="px-4 py-3">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium capitalize">
                      {school.schoolName}
                    </td>
                    <td className="px-4 py-3 capitalize">{school.projectName}</td>
                    <td className="px-4 py-3 font-semibold">₹{school.amount}</td>
                    <td className="px-4 py-3 text-green-600 font-medium">
                      ₹{paidAmount}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`font-medium ${
                          pendingAmount === 0
                            ? "text-green-600"
                            : pendingAmount === school.amount
                            ? "text-red-600"
                            : "text-yellow-600"
                        }`}
                      >
                        ₹{pendingAmount}
                      </span>
                    </td>
                    <td className="px-4 py-3">{school.contact1}</td>
                    <td className="px-4 py-3">{school.contact2 || "-"}</td>
                    <td className="px-4 py-3">{school.address}</td>
                    <td className="px-4 py-3 text-center flex gap-2 justify-center">
                      <button
                        onClick={() =>
                          navigate(`/dashboard/transactions/${school._id}`, {
                            state: { school },
                          })
                        }
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md"
                      >
                        Manage
                      </button>
                      <button
                        onClick={() => handleEdit(school)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-md"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(school._id)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="10" className="text-center py-4 text-gray-500">
                  No schools added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6 relative">
            <h2 className="text-xl font-semibold mb-4">
              {editingId ? "Edit School" : "Create School"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              {[
                "schoolName",
                "contact1",
                "contact2",
                "projectName",
                "amount",
                "address",
              ].map((field) => (
                <input
                  key={field}
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  placeholder={field.replace(/([A-Z])/g, " $1")}
                  required={field !== "contact2"}
                  className="w-full border px-3 py-2 rounded-md"
                />
              ))}
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {loading ? "Saving..." : editingId ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}